import { Alert, Anchor, Box, Button, Card, Grid, Loader, Stack, Stepper, Text, Title } from '@mantine/core';
import { IconCheck, IconExternalLink, IconPlugConnectedX } from '@tabler/icons-react';
import queryString from 'query-string';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import Metadata from '../../components/Metadata';
import ActivateDomainAppEmbedTab from '../../components/Onboarding/ActivateDomainAppEmbedTab';
import ActivateDomainFinishedTab from '../../components/Onboarding/ActivateDomainFinishedTab';
import ActivateDomainPaymentTab from '../../components/Onboarding/ActivateDomainPaymentTab';
import ActivateDomainSubscriptionTab from '../../components/Onboarding/ActivateDomainSubscriptionTab';
import ActivateDomainTermsTab from '../../components/Onboarding/ActivateDomainTermsTab';
import ActivateDomainBillingInformationTab from '../../components/Onboarding/ActiveDomainBillingInformationTab';
import { Page } from '../../constants/pageTitles';
import { isAppUninstalled, SHOPIFY_APP_STORE_URL } from '../../helpers/domainSuspension';
import useFetch from '../../helpers/useFetch';
import mainLogo from '../../logo.png';

// Purely a function of the domain: which steps exist and which are done is the
// domain's state, never who is looking at it. It used to take the user so that a
// @releva.ai address could mark the terms step complete for staff; that made the
// wizard report a different thing to different people, and staff can now move
// between steps directly instead. Keep it that way — if a step needs to behave
// differently for internal users, that belongs in the navigation rules, not in
// what the wizard claims is done.
export const activateStatusState = (domain) => {
  const tabs = [
    { name: 'terms', status: 'inProgress' },
    { name: 'subscription', status: 'inProgress' },
    { name: 'billingInformation', status: 'inProgress' },
    { name: 'payment', status: 'inProgress' },
    { name: 'appEmbed', status: 'inProgress' },
    { name: 'finished', status: 'inProgress' },
  ];

  if (domain.termsAcceptedAt && domain.pricingAcceptedAt && domain.velocity && domain.category) {
    tabs[0].status = 'completed';
  }

  if (domain.domainSubscriptions.filter((s) => s.status === 'active').length > 0) {
    tabs[1].status = 'completed';
  }

  if (
    domain.account.addressLine1 &&
    domain.account.companyName &&
    domain.account.city &&
    domain.account.postalCode &&
    domain.account.country
  ) {
    tabs[2].status = 'completed';
  } else {
    tabs[2].status = 'inProgress';
  }

  if (domain.account.paymentMethod) {
    tabs[3].status = 'completed';
  }

  // Only a confirmed check completes this step. null (never checked) and false
  // both leave it in progress, so the wizard stops on it.
  if (domain.appEmbedActive === true) {
    tabs[4].status = 'completed';
  }

  if (domain.integrationActive) {
    tabs[5].status = 'completed';
  }

  const omitted = [];
  if (domain.account.partner?.billingManagement === 'external') {
    omitted.push('billingInformation', 'payment');
  }
  if (domain.platform !== 'shopify') {
    omitted.push('appEmbed');
  }

  return tabs.filter((t) => !omitted.includes(t.name));
};

const STEP_LABELS = {
  terms: 'Welcome',
  subscription: 'Subscription',
  billingInformation: 'Billing Information',
  payment: 'Payment Method',
  appEmbed: 'Activation',
  finished: 'Finished',
};

const isInternalUser = (user) => user.email.endsWith('@releva.ai');

// The wizard is forward-only for merchants: no step but the current one is
// selectable, so a step cannot be revisited or skipped ahead to. Releva staff walking a
// merchant through the flow - or checking a step they were never routed to,
// since DomainPage does not redirect @releva.ai users into the wizard at all -
// need to move around it freely.
//
// `finished` stays gated for everyone regardless: its button calls
// POST /accounts/:id/activate, which sets running, integrationActive and
// productSyncSchedule on EVERY domain in the account. Jumping straight to it
// would start a trial across an account in one click.
export const canLeaveForTab = (tabs, name, internal) => {
  if (name !== 'finished') {
    // A step the merchant has already settled — completed, or explicitly
    // skipped — is theirs to go back to. Only steps still ahead of them stay
    // shut, so the wizard cannot be jumped forward. Without this a confirmed
    // Activation step was one nobody could ever look at: the wizard opens past
    // it, and every route back was closed.
    return internal || tabs.some((t) => t.name === name && t.status !== 'inProgress');
  }
  return tabs.filter((t) => t.name !== 'finished').every((t) => t.status === 'completed');
};

const ActivateDomainPage = () => {
  const { accountId, domainId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fetch = useFetch();

  const { user } = useSelector((state) => state.auth);
  const [account, setAccount] = useState();
  const [domain, setDomain] = useState();

  const [tabs, setTabs] = useState();
  const [mainTab, setMainTab] = useState();

  // `?tab=<name>` opens the wizard on a named step, so a support conversation or
  // a post-plan-approval redirect can point at one directly. Honoured once, on
  // the first render that has tabs: after that the usual
  // first-incomplete-step progression takes over, so completing a step still
  // advances rather than snapping back to the requested one.
  const requestedTab = queryString.parse(location.search).tab;
  const requestedTabApplied = useRef(false);

  useEffect(() => {
    let account = user.userRoles
      .filter((role) => role.account)
      .map((role) => role.account)
      .find((account) => account.id === parseInt(accountId));

    if (!account) {
      account = user.userRoles.find((r) => r.account)?.account;
      if (!account) {
        navigate('/');
        return;
      }
    }

    let domain = account.domains.find((domain) => domain.id === parseInt(domainId));
    if (!domain) {
      domain = account.domains[0];
    }

    fetch(`/api/admin/accounts/${account.id}/domains/${domain.id}`, {
      method: 'GET',
    }).then(({ domain: fullDomain }) => {
      setAccount(fullDomain.account);
      setDomain(fullDomain);
      setTabs(activateStatusState(fullDomain));
    });
  }, [accountId, domainId, user.userRoles, navigate, user, fetch]);

  // Stable: the app-embed tab reports a confirmed check from an effect, which
  // would re-run on every render if this identity changed.
  const handleChangeTab = useCallback((tab) => {
    setTabs((prev) => {
      return prev.map((prev) => (prev.name === tab.name ? tab : prev));
    });
  }, []);

  useEffect(() => {
    if (!tabs) {
      return;
    }

    if (
      !requestedTabApplied.current &&
      requestedTab &&
      tabs.some((t) => t.name === requestedTab) &&
      // A deep link may not do what a click may not do — including jumping over a
      // step that is not finished. It used to guard only `finished`, so
      // /activate?tab=appEmbed on a fresh domain dropped the merchant onto
      // Activation with Terms unaccepted, and completing it then bounced them
      // back to Welcome, which is where they should have started.
      canLeaveForTab(tabs, requestedTab, isInternalUser(user))
    ) {
      requestedTabApplied.current = true;
      setMainTab(requestedTab);
      return;
    }

    setMainTab((tabs.find((tab) => tab.status === 'inProgress') || tabs.at(-1)).name);
  }, [tabs, requestedTab, user]);

  const successStatusIcon = <IconCheck size={14} style={{ color: 'var(--mantine-color-green-6)' }} />;

  if (tabs === undefined || mainTab === undefined) {
    return <Loader />;
  }

  // An uninstall clears integrationActive, which is what routes merchants here
  // in the first place (see DomainPage) - but there is nothing for them to do
  // here: picking a plan hands off to Shopify to approve a charge for an app
  // that is no longer installed, and the Activation step checks a theme we can
  // no longer read. Every path through the wizard is a dead end until the app is
  // back, so say that instead of walking them into one.
  if (isAppUninstalled(domain)) {
    return (
      <div>
        <Metadata title={`${Page['activateDomain']}`} />
        <Grid pt={60}>
          <Grid.Col span={{ base: 12, md: 6 }} offset={{ md: 3 }}>
            <Card withBorder>
              <Card.Section inheritPadding pt="sm" pb="xl">
                <Box ta="center" mb="md">
                  <img src={mainLogo} alt="Releva" style={{ height: 60 }} />
                </Box>
                <Stack align="center" gap="md">
                  <IconPlugConnectedX size={56} stroke={1.5} color="var(--mantine-color-red-6)" />
                  <Title order={3} ta="center">
                    Releva is not installed in your store
                  </Title>
                  <Text c="dimmed" ta="center">
                    The Releva app was removed from {domain.name}, so your subscription was cancelled and everything
                    Releva was running for you has stopped. Your data, campaigns and settings are all still here —
                    reinstall the app and you can carry on from where you left off.
                  </Text>
                  <Button
                    component="a"
                    href={SHOPIFY_APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    rightSection={<IconExternalLink size={14} />}
                  >
                    Reinstall from the Shopify App Store
                  </Button>
                  <Alert color="gray" variant="light" w="100%">
                    Did not mean to uninstall, or need a hand putting it back? Email{' '}
                    <Anchor href="mailto:support@releva.ai">support@releva.ai</Anchor> and we will help.
                  </Alert>
                </Stack>
              </Card.Section>
            </Card>
          </Grid.Col>
        </Grid>
      </div>
    );
  }

  // Stepper addresses steps by index, but everything else here works by name,
  // because which steps are present depends on the domain and the partner.
  const activeIndex = Math.max(
    tabs.findIndex((t) => t.name === mainTab),
    0
  );
  const internal = isInternalUser(user);
  const appEmbedTab = tabs.find((t) => t.name === 'appEmbed');
  const appEmbedIncomplete = appEmbedTab ? appEmbedTab.status !== 'completed' : false;
  // appEmbedTab.appEmbedActive is what the live check in ActivateDomainAppEmbedTab
  // actually answered, threaded up via handleChangeTab: true/false only if a
  // check answered, null (or absent) otherwise. That is the answer the merchant
  // watched happen, unlike `domain.appEmbedActive`, which is whatever the wizard
  // fetched once at mount and never refreshes. Only a real `false` earns the
  // blunt wording on Finished.
  const appEmbedConfirmedOff = appEmbedTab?.appEmbedActive === false;

  return (
    <div>
      <Metadata title={`${Page['activateDomain']}`} />
      <Grid pt={60}>
        <Grid.Col span={2}></Grid.Col>
        <Grid.Col span={8}>
          <Card withBorder>
            <Card.Section inheritPadding pt="sm" pb="xl">
              <Box ta="center" mb="md">
                <img src={mainLogo} alt="Releva" style={{ height: 60 }} />
              </Box>

              <Stepper
                active={activeIndex}
                onStepClick={(index) => setMainTab(tabs[index].name)}
                // Per-step, not blanket: a completed step behind the merchant is
                // still not theirs to revisit, and `finished` is nobody's to
                // jump to early.
                allowNextStepsSelect={false}
                mb={0}
              >
                {tabs.map(({ name, status }, index) => (
                  <Stepper.Step
                    key={name}
                    label={STEP_LABELS[name]}
                    allowStepSelect={canLeaveForTab(tabs, name, internal)}
                    // Stepper derives "completed" from position alone — every
                    // step before the active one renders a tick, whatever its
                    // real status. Completion here is state, not order, and a
                    // tick against a step the merchant never did is a lie the
                    // wizard tells about their own account. `completedIcon`
                    // overrides the positional tick (the `icon` prop cannot:
                    // StepperStep drops it outright for a completed state),
                    // while `icon` covers a genuinely completed step sitting
                    // ahead of the cursor.
                    completedIcon={status === 'completed' ? undefined : index + 1}
                    icon={status === 'completed' ? successStatusIcon : undefined}
                  >
                    <div style={{ marginTop: 'var(--mantine-spacing-md)' }}>
                      {name === 'terms' && (
                        <ActivateDomainTermsTab domain={domain} account={account} handleChangeTab={handleChangeTab} />
                      )}
                      {name === 'subscription' && (
                        <ActivateDomainSubscriptionTab domain={domain} handleChangeTab={handleChangeTab} />
                      )}
                      {name === 'billingInformation' && (
                        <ActivateDomainBillingInformationTab accountId={account.id} handleChangeTab={handleChangeTab} />
                      )}
                      {name === 'payment' && (
                        <ActivateDomainPaymentTab domain={domain} handleChangeTab={handleChangeTab} />
                      )}
                      {name === 'appEmbed' && (
                        <ActivateDomainAppEmbedTab
                          domain={domain}
                          active={mainTab === 'appEmbed'}
                          handleChangeTab={handleChangeTab}
                        />
                      )}
                      {name === 'finished' && (
                        <ActivateDomainFinishedTab
                          domain={domain}
                          appEmbedIncomplete={appEmbedIncomplete}
                          appEmbedConfirmedOff={appEmbedConfirmedOff}
                        />
                      )}
                    </div>
                  </Stepper.Step>
                ))}
              </Stepper>
            </Card.Section>
          </Card>
        </Grid.Col>
        <Grid.Col span={2}></Grid.Col>
      </Grid>
    </div>
  );
};

export default ActivateDomainPage;
